/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import {
  Box,
  Button,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import { CustomFilterV2, DialogDefaultV2, TableV2 } from '../../../components/v2';
import * as actions from '../../../store/controleFrequencia/actions';
import * as Roles from '../../../constants/role';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import { JORNADA } from 'constants/modules';
import { hasModuleConfiguration } from '../../../helpers/moduleConfigurationPermission';
import { VINICIUS_ADMIN_EMAIL } from 'utils';
import { formatWithDay } from 'utils/date-format';
import Formulario from './Formulario';

const getDefaultFilter = () => ({
  inicio: moment().subtract(1, 'month').startOf('month').valueOf(),
  fim: moment().endOf('month').valueOf(),
  tipoControle: 'CONTROLE_JORNADA',
});

const ControleFrequenciaV2 = ({
  authData,
  exportControleFrequencia,
  deleteControleFrequencia,
  downloadControleFrequencia,
  moduleConfigurations,
  history,
}) => {
  const isSuperAdmin = authData?.role === Roles.SUPER_ADMIN;
  const useNovoFluxo = !isSuperAdmin && hasModuleConfiguration(
    moduleConfigurations,
    authData,
    JORNADA,
  );

  useEffect(() => {
    if (useNovoFluxo) {
      history.replace('/v2/jornada-medicao');
    }
  }, [useNovoFluxo, history]);

  const [open, setOpen] = useState(false);
  const [openDownload, setOpenDownload] = useState(false);
  const [id, setId] = useState(null);
  const [localFilter, setLocalFilter] = useState(getDefaultFilter);
  const [openExport, setOpenExport] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [reload, setReload] = useState(false);

  const defaultFilterMemo = useMemo(() => getDefaultFilter(), []);

  const apiFilter = useMemo(
    () => ({ ...localFilter, tipoControle: 'CONTROLE_JORNADA' }),
    [localFilter],
  );

  const isAdmin = () =>
    authData && (authData.role === Roles.ADMIN || authData.role === Roles.SUPER_ADMIN);

  const onEdit = (rowId) => {
    setOpen(true);
    setId(rowId);
  };

  const onAdd = () => {
    setOpen(true);
    setId(null);
  };

  const onClose = (isSave) => {
    setOpen(false);
    setId(null);
    if (isSave) {
      setReload((current) => !current);
    }
  };

  const headerActions = [
    {
      label: 'Exportar',
      onClick: () => setOpenExport(true),
      startIcon: <FileDownloadOutlinedIcon />,
      variant: 'outlined',
    },
  ];

  if (authData?.email === VINICIUS_ADMIN_EMAIL) {
    headerActions.push({
      label: 'Download Zip',
      onClick: () => setOpenDownload(true),
      startIcon: <FolderZipOutlinedIcon />,
      variant: 'outlined',
    });
  }

  if (isAdmin()) {
    headerActions.unshift({
      label: 'Novo',
      onClick: onAdd,
      startIcon: <AddIcon />,
      variant: 'contained',
    });
  }

  const columns = [
    {
      field: 'data',
      headerName: 'Data',
      flex: 0.5,
      renderCell: (params) => formatWithDay(params.row.data),
    },
    { field: 'tipoServicoNome', headerName: 'Tipo de Serviço', flex: 0.7 },
    { field: 'cidade', headerName: 'Cidade', flex: 0.5 },
    {
      field: 'inspetorNome',
      headerName: 'Inspetor e Auxiliar',
      flex: 0.8,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">{params.row.inspetorNome}</Typography>
          {params.row.inspetorAuxiliarNome && (
            <Typography variant="body2">{params.row.inspetorAuxiliarNome}</Typography>
          )}
        </Box>
      ),
    },
    { field: 'jornadaTotal', headerName: 'Total da Jornada', flex: 0.5 },
  ];

  if (useNovoFluxo) return null;

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography variant="h4">Controle de Jornadas</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <TableV2
          useApi
          fetchUrl="pontos/filter"
          paginationUrl="/v2/controle-frequencia"
          filterData={apiFilter}
          refreshKey={reload}
          columns={columns}
          rowHeight={56}
          onEdit={isAdmin() ? onEdit : undefined}
          onDelete={
            isAdmin()
              ? (rowId) => deleteControleFrequencia(rowId, () => setReload((c) => !c))
              : undefined
          }
          headerActions={headerActions}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={defaultFilterMemo}
          appliedFilter={localFilter}
          fields={[
            { field: 'useDates' },
            {
              field: 'useUsuarios',
              md: 12,
              xs: 12,
              multiSelect: true,
              grupos: [Roles.ADMIN, Roles.INSPETOR],
            },
            { field: 'useTipoServico', md: 6, xs: 12 },
            { field: 'useCodigo', codigoLabel: 'Cidade', md: 6, xs: 12 },
          ]}
          onFilter={(filterData) => {
            const next = { ...filterData, tipoControle: 'CONTROLE_JORNADA' };
            setLocalFilter(next);
            setOpenFilter(false);
          }}
        />

        <Formulario id={id} open={open} onClose={onClose} />

        <DialogDefaultV2
          open={openExport}
          handlerCancel={() => setOpenExport(false)}
          handlerConfirm={() => exportControleFrequencia(apiFilter, () => setOpenExport(false))}
          title="Confirmação"
          confirmMessage="Exportar"
        >
          <DialogContentText>
            Deseja exportar todas as informações filtradas?
          </DialogContentText>
        </DialogDefaultV2>

        <DialogDefaultV2
          open={openDownload}
          handlerCancel={() => setOpenDownload(false)}
          handlerConfirm={() => {
            downloadControleFrequencia(apiFilter, () => setOpenDownload(false));
          }}
          title="Confirmação"
          confirmMessage="Sim"
          cancelMessage="Não"
        >
          <DialogContentText>
            Deseja baixar todas os arquivos das notas filtradas?
          </DialogContentText>
        </DialogDefaultV2>
      </Stack>
    </Box>
  );
};

ControleFrequenciaV2.propTypes = {
  authData: PropTypes.object,
  exportControleFrequencia: PropTypes.func.isRequired,
  deleteControleFrequencia: PropTypes.func.isRequired,
  downloadControleFrequencia: PropTypes.func.isRequired,
  moduleConfigurations: PropTypes.array,
  history: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...actions,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ControleFrequenciaV2));
